import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  AutoFixHigh as AutoFixIcon,
  Code as CodeIcon,
  GridView as GridIcon,
  ViewCompact as CompactIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
} from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { ImportedNodeType as EnhancedImportedNodeType } from '../../types/nodeImport.types';

// Component types available in the palette
const COMPONENT_TYPES = [
  { type: 'text', label: 'Text Field', icon: 'T' },
  { type: 'textarea', label: 'Text Area', icon: '¶' },
  { type: 'number', label: 'Number', icon: '123' },
  { type: 'select', label: 'Select', icon: '▼' },
  { type: 'multiselect', label: 'Multi Select', icon: '☑' },
  { type: 'checkbox', label: 'Checkbox', icon: '☐' },
  { type: 'switch', label: 'Switch', icon: '○' },
  { type: 'radio', label: 'Radio Group', icon: '◎' },
  { type: 'date', label: 'Date', icon: '📅' },
  { type: 'datetime', label: 'DateTime', icon: '⏰' },
  { type: 'file', label: 'File Upload', icon: '📎' },
  { type: 'password', label: 'Password', icon: '🔒' },
  { type: 'color', label: 'Color', icon: '🎨' },
  { type: 'json', label: 'JSON', icon: '{ }' },
  { type: 'code', label: 'Code', icon: '</>' },
  { type: 'richtext', label: 'Rich Text', icon: '✎' },
  { type: 'label', label: 'Label', icon: 'L' },
  { type: 'divider', label: 'Divider', icon: '—' },
  { type: 'section', label: 'Section', icon: '▭' },
  { type: 'tabs', label: 'Tabs', icon: '☰' },
  { type: 'button', label: 'Button', icon: 'Btn' },
  { type: 'slider', label: 'Slider', icon: '↔' },
  { type: 'rating', label: 'Rating', icon: '★' },
  { type: 'autocomplete', label: 'Autocomplete', icon: '🔍' },
];

interface ComponentItem {
  id: string;
  type: string;
  label: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  properties: any;
  boundProperty?: string;
}

interface LayoutEditorProps {
  layout: any;
  nodeData?: EnhancedImportedNodeType | null;
  onChange: (layout: any) => void;
  onAutoGenerate?: () => void | Promise<void>;
  onGenerateReact?: () => void;
}

// Draggable component palette item
const PaletteItem: React.FC<{ type: string; label: string; icon: string }> = ({ type, label, icon }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'COMPONENT',
    item: { type, label, isNew: true },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <Box
      ref={drag}
      sx={{
        p: 1.5,
        m: 0.5,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        cursor: 'move',
        opacity: isDragging ? 0.5 : 1,
        bgcolor: 'background.paper',
        '&:hover': {
          bgcolor: 'action.hover',
          borderColor: 'primary.main',
        },
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'primary.light',
          color: 'primary.contrastText',
          borderRadius: 1,
          fontSize: 12,
          fontWeight: 'bold',
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2" noWrap>{label}</Typography>
    </Box>
  );
};

// Draggable canvas component
const CanvasComponent: React.FC<{
  component: ComponentItem;
  onMove: (id: string, x: number, y: number) => void;
  onSelect: (component: ComponentItem) => void;
  onDelete: (id: string) => void;
  isSelected: boolean;
}> = ({ component, onMove, onSelect, onDelete, isSelected }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'CANVAS_COMPONENT',
    item: { id: component.id, type: component.type },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  return (
    <Box
      ref={drag}
      onClick={() => onSelect(component)}
      sx={{
        position: 'absolute',
        left: component.position.x,
        top: component.position.y,
        width: component.size.width,
        minHeight: component.size.height,
        p: 1,
        border: '2px solid',
        borderColor: isSelected ? 'primary.main' : 'transparent',
        borderRadius: 1,
        bgcolor: isSelected ? 'primary.light' : 'background.paper',
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        boxShadow: isSelected ? 2 : 0,
        '&:hover': {
          borderColor: 'primary.light',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" fontWeight="bold">
          {component.label}
        </Typography>
        {isSelected && (
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(component.id); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      
      {/* Component preview */}
      <Box sx={{ pointerEvents: 'none' }}>
        {renderComponentPreview(component)}
      </Box>
      
      {component.boundProperty && (
        <Chip 
          label={component.boundProperty} 
          size="small" 
          color="info" 
          sx={{ mt: 0.5, fontSize: 10 }}
        />
      )}
    </Box>
  );
};

// Render component preview based on type
const renderComponentPreview = (component: ComponentItem) => {
  const { type, label } = component;
  
  switch (type) {
    case 'text':
    case 'number':
    case 'password':
      return (
        <TextField
          fullWidth
          size="small"
          label={label}
          variant="outlined"
          disabled
        />
      );
    case 'textarea':
      return (
        <TextField
          fullWidth
          size="small"
          label={label}
          multiline
          rows={3}
          variant="outlined"
          disabled
        />
      );
    case 'select':
      return (
        <FormControl fullWidth size="small">
          <InputLabel>{label}</InputLabel>
          <Select disabled value="">
            <MenuItem value="">Select...</MenuItem>
          </Select>
        </FormControl>
      );
    case 'checkbox':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <input type="checkbox" disabled />
          <Typography variant="body2" sx={{ ml: 1 }}>{label}</Typography>
        </Box>
      );
    case 'switch':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <input type="checkbox" disabled style={{ width: 40, height: 20 }} />
          <Typography variant="body2" sx={{ ml: 1 }}>{label}</Typography>
        </Box>
      );
    case 'label':
      return <Typography variant="body1">{label}</Typography>;
    case 'divider':
      return <Divider />;
    case 'button':
      return <Button variant="contained" size="small" disabled>{label}</Button>;
    default:
      return (
        <Box sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" color="textSecondary">
            {type}: {label}
          </Typography>
        </Box>
      );
  }
};

// Canvas drop zone
const Canvas: React.FC<{
  components: ComponentItem[];
  onDrop: (item: any, x: number, y: number) => void;
  onMove: (id: string, x: number, y: number) => void;
  onSelect: (component: ComponentItem) => void;
  onDelete: (id: string) => void;
  selectedId: string | null;
}> = ({ components, onDrop, onMove, onSelect, onDelete, selectedId }) => {
  const canvasRef = useRef<HTMLDivElement | null>(null);
  
  const [, drop] = useDrop({
    accept: ['COMPONENT', 'CANVAS_COMPONENT'],
    drop: (item: any, monitor) => {
      const offset = monitor.getClientOffset();
      if (offset && canvasRef.current) {
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const x = offset.x - canvasRect.left;
        const y = offset.y - canvasRect.top;
        
        if (item.isNew) {
          onDrop(item, x, y);
        } else if (item.id) {
          onMove(item.id, x, y);
        }
      }
    },
  });

  const setCanvasRef = useCallback(
    (node: HTMLDivElement | null) => {
      canvasRef.current = node;
      drop(node as any);
    },
    [drop]
  );

  return (
    <Box
      ref={setCanvasRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 600,
        bgcolor: 'grey.50',
        backgroundImage: `
          linear-gradient(to right, #e0e0e0 1px, transparent 1px),
          linear-gradient(to bottom, #e0e0e0 1px, transparent 1px)
        `,
        backgroundSize: '20px 20px',
        overflow: 'auto',
      }}
    >
      {components.map((component) => (
        <CanvasComponent
          key={component.id}
          component={component}
          onMove={onMove}
          onSelect={onSelect}
          onDelete={onDelete}
          isSelected={selectedId === component.id}
        />
      ))}
    </Box>
  );
};

export const LayoutEditor: React.FC<LayoutEditorProps> = ({
  layout,
  nodeData,
  onChange,
  onAutoGenerate,
  onGenerateReact
}) => {
  const sortComponentsByCanvasPosition = useCallback((items: ComponentItem[]) => {
    return [...items].sort((a, b) => {
      const ay = Number(a?.position?.y ?? 0);
      const by = Number(b?.position?.y ?? 0);
      if (ay !== by) return ay - by;

      const ax = Number(a?.position?.x ?? 0);
      const bx = Number(b?.position?.x ?? 0);
      if (ax !== bx) return ax - bx;

      return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
    });
  }, []);

  const [components, setComponents] = useState<ComponentItem[]>(
    () => sortComponentsByCanvasPosition(layout?.components || [])
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  
  const selectedComponent = components.find(c => c.id === selectedId);
  
  // Handle dropping a new component
  const handleDrop = useCallback((item: any, x: number, y: number) => {
    const newComponent: ComponentItem = {
      id: `comp_${Date.now()}`,
      type: item.type,
      label: item.label,
      position: { x: Math.round(x / 10) * 10, y: Math.round(y / 10) * 10 },
      size: { width: 300, height: 56 },
      properties: {},
    };
    
    const newComponents = sortComponentsByCanvasPosition([...components, newComponent]);
    setComponents(newComponents);
    onChange({ components: newComponents });
    setSelectedId(newComponent.id);
  }, [components, onChange, sortComponentsByCanvasPosition]);
  
  // Handle moving a component
  const handleMove = useCallback((id: string, x: number, y: number) => {
    const movedComponents = components.map(c =>
      c.id === id
        ? { ...c, position: { x: Math.round(x / 10) * 10, y: Math.round(y / 10) * 10 } }
        : c
    );
    const newComponents = sortComponentsByCanvasPosition(movedComponents);
    setComponents(newComponents);
    onChange({ components: newComponents });
  }, [components, onChange, sortComponentsByCanvasPosition]);
  
  // Handle deleting a component
  const handleDelete = useCallback((id: string) => {
    const newComponents = components.filter(c => c.id !== id);
    setComponents(newComponents);
    onChange({ components: newComponents });
    if (selectedId === id) setSelectedId(null);
  }, [components, onChange, selectedId]);
  
  // Auto-generate layout from node properties
  const handleAutoGenerate = useCallback(() => {
    if (!nodeData?.properties) return;
    
    const newComponents: ComponentItem[] = nodeData.properties.map((prop: any, index: number) => {
      const typeMap: Record<string, string> = {
        string: 'text',
        number: 'number',
        boolean: 'switch',
        options: 'select',
        multiOptions: 'multiselect',
        json: 'json',
        dateTime: 'datetime',
        color: 'color',
        file: 'file',
        password: 'password',
      };
      
      const col = index % 2;
      const row = Math.floor(index / 2);
      
      return {
        id: `comp_${prop.name}_${Date.now()}`,
        type: typeMap[prop.type] || 'text',
        label: prop.displayName || prop.name,
        position: { x: 20 + col * 420, y: 20 + row * 80 },
        size: { width: 400, height: 56 },
        properties: prop,
        boundProperty: prop.name,
      };
    });
    
    const sortedComponents = sortComponentsByCanvasPosition(newComponents);
    setComponents(sortedComponents);
    onChange({ components: sortedComponents });
  }, [nodeData, onChange, sortComponentsByCanvasPosition]);
  
  // Update component settings
  const handleUpdateComponent = (updates: Partial<ComponentItem>) => {
    if (!selectedComponent) return;
    
    const updatedComponents = components.map(c =>
      c.id === selectedId ? { ...c, ...updates } : c
    );
    const newComponents = sortComponentsByCanvasPosition(updatedComponents);
    setComponents(newComponents);
    onChange({ components: newComponents });
  };
  
  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{ height: '100%', display: 'flex' }}>
        {/* Component Palette */}
        <Paper
          sx={{
            width: 220,
            height: '100%',
            overflow: 'auto',
            borderRight: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Components
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Drag to canvas
            </Typography>
          </Box>
          
          <Divider />
          
          <Box sx={{ p: 1 }}>
            {COMPONENT_TYPES.map((comp) => (
              <PaletteItem
                key={comp.type}
                type={comp.type}
                label={comp.label}
                icon={comp.icon}
              />
            ))}
          </Box>
        </Paper>
        
        {/* Canvas */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Toolbar */}
          <Paper sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              size="small"
              startIcon={<AutoFixIcon />}
              onClick={() => {
                if (onAutoGenerate) {
                  onAutoGenerate();
                  return;
                }
                handleAutoGenerate();
              }}
              disabled={!nodeData}
            >
              Auto-Generate
            </Button>
            
            <Button
              size="small"
              startIcon={<CodeIcon />}
              onClick={onGenerateReact}
              disabled={components.length === 0}
            >
              Generate React
            </Button>
            
            <Divider orientation="vertical" flexItem />
            
            <Typography variant="caption" color="textSecondary">
              {components.length} components
            </Typography>
            
            {selectedComponent && (
              <>
                <Divider orientation="vertical" flexItem />
                <Chip
                  size="small"
                  label={selectedComponent.label}
                  color="primary"
                  onDelete={() => setSelectedId(null)}
                />
                <IconButton size="small" onClick={() => setShowSettings(true)}>
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Paper>
          
          {/* Canvas Area */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            <Canvas
              components={components}
              onDrop={handleDrop}
              onMove={handleMove}
              onSelect={(component) => setSelectedId(component.id)}
              onDelete={handleDelete}
              selectedId={selectedId}
            />
          </Box>
        </Box>
        
        {/* Properties Panel */}
        {selectedComponent && (
          <Paper
            sx={{
              width: 280,
              height: '100%',
              overflow: 'auto',
              borderLeft: '1px solid',
              borderColor: 'divider',
              p: 2,
            }}
          >
            <Typography variant="subtitle2" gutterBottom>
              Properties
            </Typography>
            
            <TextField
              fullWidth
              size="small"
              label="Label"
              value={selectedComponent.label}
              onChange={(e) => handleUpdateComponent({ label: e.target.value })}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              size="small"
              label="Bound Property"
              value={selectedComponent.boundProperty || ''}
              onChange={(e) => handleUpdateComponent({ boundProperty: e.target.value })}
              sx={{ mb: 2 }}
              helperText="Node property this component binds to"
            />
            
            <Typography variant="caption" color="textSecondary">
              Position: ({selectedComponent.position.x}, {selectedComponent.position.y})
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="textSecondary">
                Size
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <TextField
                  size="small"
                  label="Width"
                  type="number"
                  value={selectedComponent.size.width}
                  onChange={(e) => handleUpdateComponent({
                    size: { ...selectedComponent.size, width: parseInt(e.target.value) }
                  })}
                />
                <TextField
                  size="small"
                  label="Height"
                  type="number"
                  value={selectedComponent.size.height}
                  onChange={(e) => handleUpdateComponent({
                    size: { ...selectedComponent.size, height: parseInt(e.target.value) }
                  })}
                />
              </Box>
            </Box>
          </Paper>
        )}
        
        {/* Settings Dialog */}
        <Dialog open={showSettings} onClose={() => setShowSettings(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Component Settings</DialogTitle>
          <DialogContent>
            {selectedComponent && (
              <>
                <TextField
                  fullWidth
                  label="Label"
                  value={selectedComponent.label}
                  onChange={(e) => handleUpdateComponent({ label: e.target.value })}
                  margin="normal"
                />
                
                <TextField
                  fullWidth
                  label="Placeholder"
                  value={selectedComponent.properties?.placeholder || ''}
                  onChange={(e) => handleUpdateComponent({
                    properties: { ...selectedComponent.properties, placeholder: e.target.value }
                  })}
                  margin="normal"
                />
                
                <TextField
                  fullWidth
                  label="Helper Text"
                  value={selectedComponent.properties?.helperText || ''}
                  onChange={(e) => handleUpdateComponent({
                    properties: { ...selectedComponent.properties, helperText: e.target.value }
                  })}
                  margin="normal"
                />
                
                <FormControl fullWidth margin="normal">
                  <InputLabel>Bound Property</InputLabel>
                  <Select
                    value={selectedComponent.boundProperty || ''}
                    onChange={(e) => handleUpdateComponent({ boundProperty: e.target.value })}
                  >
                    <MenuItem value="">None</MenuItem>
                    {nodeData?.properties?.map((prop: any) => (
                      <MenuItem key={prop.name} value={prop.name}>
                        {prop.displayName || prop.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSettings(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DndProvider>
  );
};

export default LayoutEditor;
